// BoxIoService.js (or whatever this file is named)

import io from 'socket.io-client';
import { Subject, BehaviorSubject, skip } from 'rxjs';
import {
  getDefaultControllerState,
  newState,
  MODE,
  defaultControllerState,
} from './defaultControllerState';
import WebSerial, { translatePins } from './webSerial/WebSerial';
import { sleep, deep_copy } from '@/util';

// ["D0", ..., "D23", "R0", ..., "R15"]
const digitalOutputs = [
  ...[...Array(24).keys()].map((i) => 'D' + i),
  ...[...Array(16).keys()].map((i) => 'R' + i),
];

// ["A0", ..., "A15"]
const analogInputs = [...Array(16).keys()].map((i) => 'A' + i);

const isHardwarePin = (name) => translatePins[name] != null;

const isHardwareOutputType = (type) =>
  type === 'digital_output' ||
  type === 'relay_output' ||
  type === 'pwm_output' ||
  type === 'servo_output';

const isVirtualWritable = (type) => type === 'virtual_input';

const isWritable = (pin) =>
  isVirtualWritable(pin?.type) || isHardwareOutputType(pin?.type);

const isHardwareWritable = (pin, name) =>
  isHardwarePin(name) && isHardwareOutputType(pin?.type);

/**
 * (Optional / legacy) Socket-based backend channel.
 * Kept here since your original file had it, but BoxIoService uses WebSerial in constructor.
 * If you still need it: set `this.netWorkService = new NetworkService(this)` instead of WebSerial.
 */
class NetworkService {
  constructor(parent) {
    this.socket = io('ws://localhost:3099', {
      forceNew: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 60,
      path: '/socket.io',
      transports: ['websocket'],
      autoConnect: true,
    });

    this.socket.on('state', parent.setState);
    this.socket.on('onDigital', parent.setDigital);
    this.socket.on('onAnalog', parent.setAnalog);
    this.socket.on('onStream', parent.setStream);
    this.socket.on('onModeAnalog', parent.setModeAnalog);
    this.socket.on('onModeDigital', parent.setModeDigital);
    this.socket.on('ovlWarning', parent.setOvlWarning);
    this.socket.on('pwmFrequency', parent.sendPwmFrequency);
    this.socket.on('reset', parent.sendReset);

    this.socket.on('connect', () => {
      parent.initialized = true;
      parent.boxConnected.next(true);
      parent.onInitialize.next(true);
    });
    this.socket.on('connect_error', (err) => {
      parent.initialized = false;
      console.warn(`connect_error due to ${err.message}`);
    });
    this.socket.io.on('reconnection_attempt', (err) => {
      console.warn(`reconnection_attempt ${err.message}`);
    });
    this.socket.on('onOpen', function () {
      console.warn('SOCKET OPEN');
    });
  }

  emit = (...params) => {
    this.socket.emit(...params);
  };
}

/** Singleton that communicates with the box */
class BoxIoService {
  state = getDefaultControllerState();

  boxConnected = new BehaviorSubject();
  webConnected = new BehaviorSubject();
  onInitialize = new BehaviorSubject();
  OVLWarning = new BehaviorSubject();
  onState = new Subject();
  onDigital = new Subject();
  onAnalog = new Subject();
  onModeAnalog = new Subject();
  onModeDigital = new Subject();
  onStream = new Subject();
  onTrigger = new Subject();
  onStatelock = new Subject();
  uiDefaults = new Map();

  triggers = {};
  triggersActive = {};
  triggerAnyStates = {};
  triggerConditionStates = {};
  triggersStates = {};
  lockedStates = new Set();
  waitFor = [];

  pendingClearTriggerUids = new Set();
  clearTriggersTimer = null;
  resetQueue = Promise.resolve();

  // BoxIoService
  isConnected = () => {
    try {
      return !!this.boxConnected?.getValue?.();
    } catch {
      return false;
    }
  };

  // --- helpers ---------------------------------------------------------------

  assertState = (name, attrs) => {
    if (!this.state[name]) this.state[name] = newState(attrs);
  };

  assertI2C = (name, address, command) => {
    if (!defaultControllerState[name]) {
      let state = {
        name,
        state: false,
        type: 'i2c',
        address: parseFloat(address),
        command: parseFloat(command),
        mode: MODE.I2C,
        value: 0,
        pwm: false,
      };
      defaultControllerState[name] = state;
      this.state[name] = state;
    }
  };

  _restoreFromDefault = (name, fallbackType) => {
    const pin = this.state[name];
    const def = defaultControllerState?.[name];

    pin.type = def?.type ?? fallbackType;
    pin.mode = def?.mode ?? MODE.OUTPUT;

    // optional: clear servo config so a future sendServo reconfigures
    pin.servoConfigured = false;
  };

  // --- state setters ---------------------------------------------------------

  setState = (state) => {
    this.state = state;
    this.initialized = true;
    this.onState.next(state);
  };

  setDigital = (name, state) => {
    if (!this.state[name]) this.assertState(name);
    this.state[name].state = state && state !== 'false' ? true : false;
    this.onDigital.next({ name, state: this.state[name].state });
  };

  setAnalog = (name, value) => {
    if (!this.state[name]) this.assertState(name);
    this.state[name].value = value;
    this.onAnalog.next({ name, value });
  };

  setStream = (name, values) => {
    if (!this.state.streams) this.state.streams = {};
    if (!this.state.streams[name]) this.state.streams[name] = {};
    this.state.streams[name] = values;
    this.onStream.next({ name, values });
  };

  setModeAnalog = (name, mode) => {
    if (!this.state[name]) this.assertState(name);
    this.state[name].mode = mode;
    this.onModeAnalog.next({ name, mode });
  };

  setModeDigital = (name, mode) => {
    if (!this.state[name]) this.assertState(name);
    this.state[name].mode = mode;
    this.onModeDigital.next({ name, mode });
  };

  setOvlWarning = (value) => {
    this.OVLWarning.next(value);
    // this.sendReset();
  };

  // --- servo ----------------------------------------------------------------

  sendServo = (name, degrees, opts = {}) => {
    if (this.lockedStates.has(name))
      return console.error('State', name, 'is locked by a trigger');

    if (!this.state[name]) this.assertState(name);

    const pin = this.state[name];

    if (!isHardwarePin(name)) {
      return console.warn('Refusing servo on non-hardware pin', name);
    }

    const request =
      typeof degrees === 'object' && degrees !== null
        ? { ...degrees, ...opts }
        : { ...opts, angle: degrees };

    // Backward compatible:
    // - sendServo('D0', 85)
    // - sendServo('D0', { angle: 85 })
    // - sendServo('D0', { degrees: 85 })
    // - sendServo('D0', { value: 85 })
    // - sendServo('D0', { target: 85 })
    //
    // New style:
    // - sendServo('D0', { startAngle: 0, angle: 85, durationMs: 10000 })
    //
    // In the new style, "angle" is the end/target angle.
    const rawDegrees =
      request.angle ?? request.degrees ?? request.value ?? request.target;

    const parsedDegrees = Number(rawDegrees);

    if (!Number.isFinite(parsedDegrees)) {
      return console.warn('Invalid servo angle', name, degrees);
    }

    const clampDegrees = (v) =>
      Math.max(0, Math.min(180, Math.round(Number(v))));

    const previousAngle = Number(pin.value);
    const deg = clampDegrees(parsedDegrees);

    const hasStartAngle = request.startAngle != null;
    const parsedStartAngle = hasStartAngle ? Number(request.startAngle) : null;

    if (hasStartAngle && !Number.isFinite(parsedStartAngle)) {
      return console.warn('Invalid servo startAngle', name, request.startAngle);
    }

    // startAngle is optional. If omitted, we use the last known pin value when
    // we need a start angle for speed calculation.
    const startDeg = hasStartAngle
      ? clampDegrees(parsedStartAngle)
      : Number.isFinite(previousAngle)
      ? clampDegrees(previousAngle)
      : undefined;

    const rawDurationMs =
      request.durationMs ?? request.timeMs ?? request.moveMs ?? request.ms;

    const durationMs =
      rawDurationMs != null && Number.isFinite(Number(rawDurationMs))
        ? Number(rawDurationMs)
        : undefined;

    // Switch this pin into servo mode via state
    pin.type = 'servo_output';
    pin.mode = MODE.SERVO;
    pin.value = deg;
    pin.state = true;

    // Remember optional servo movement settings on the pin for UI/debugging.
    pin.servoStartAngle = startDeg;
    pin.servoEndAngle = deg;
    pin.servoDurationMs = durationMs;
    pin.servoSpeedDegPerSec = request.speedDegPerSec;
    pin.servoStepMs = request.stepMs;

    // UI updates
    this.setAnalog(name, deg);
    this.setDigital(name, true);

    // Configure once, stored on the pin object
    if (!pin.servoConfigured) {
      const minUs = request.minUs ?? 544;
      const maxUs = request.maxUs ?? 2400;
      pin.servoConfigured = true;
      pin.servoMinUs = minUs;
      pin.servoMaxUs = maxUs;

      this.netWorkService.emit('sendServoConfig', name, { minUs, maxUs });
    }

    const command = {
      angle: deg,
    };

    if (hasStartAngle) {
      command.startAngle = startDeg;
    }

    if (durationMs != null && durationMs > 0) {
      command.durationMs = durationMs;
    }

    if (request.stepMs != null) {
      const stepMs = Number(request.stepMs);
      if (Number.isFinite(stepMs) && stepMs > 0) {
        command.stepMs = stepMs;
      }
    }

    if (request.speedDegPerSec != null) {
      const speedDegPerSec = Number(request.speedDegPerSec);
      if (Number.isFinite(speedDegPerSec) && speedDegPerSec > 0) {
        command.speedDegPerSec = speedDegPerSec;
      }
    } else if (startDeg != null && durationMs != null && durationMs > 0) {
      // Auto-calculate speed from startAngle/current angle to target angle.
      command.speedDegPerSec = Math.abs(deg - startDeg) / (durationMs / 1000);
    }

    this.netWorkService.emit('sendServo', name, command);
  };

  // --- triggers/corrections --------------------------------------------------

  checkTrigger = (trigger) => {
    let active = true;
    if (trigger.conditions) {
      for (let condition of trigger.conditions) {
        let pinState = this.state[condition.pin];
        if (!pinState) {
          active = false;
          break;
        }
        if (condition.mode === 'digital') {
          if (
            condition.value !== undefined &&
            pinState.state !== condition.value
          )
            active = false;
        } else if (condition.mode === 'analog') {
          if (condition.max && pinState.value > parseFloat(condition.max))
            active = false;
          if (condition.min && pinState.value < parseFloat(condition.min))
            active = false;
        }
      }
    }
    return active;
  };

  checkCorrection = async (correction) => {
    let toUndo = {};
    if (correction.assertions) {
      for (let assertion of correction.assertions) {
        switch (assertion.action) {
          case 'set':
            switch (assertion.mode) {
              case 'analog':
                toUndo[assertion.pin] = {
                  value: this.state[assertion.pin].value,
                };
                this.sendAnalog(assertion.pin, parseFloat(assertion.value));
                break;
              case 'digital':
                toUndo[assertion.pin] = {
                  state: this.state[assertion.pin].state,
                };
                this.sendDigital(assertion.pin, assertion.value);
                break;
            }
            break;
        }
      }
    }

    // Assertions can change a measured input through a physical relay. Wait
    // beyond WebSerial's trailing input throttle before evaluating the new
    // state; otherwise consecutive correction scenarios can read the state
    // produced by the previous scenario.
    await this.waitForInputSettle();

    let active = true;
    if (correction.verifications) {
      for (let verification of correction.verifications) {
        let pinState = this.state[verification.pin];
        if (!pinState) {
          active = false;
          break;
        }
        if (verification.mode === 'digital') {
          if (pinState && pinState.state !== verification.value) active = false;
        } else if (verification.mode === 'analog') {
          if (verification.max && pinState.value > parseFloat(verification.max))
            active = false;
          if (verification.min && pinState.value < parseFloat(verification.min))
            active = false;
        }
      }
    }

    // undo actions
    for (let pin of Object.keys(toUndo)) {
      let state = toUndo[pin];
      if (state.state !== undefined && state.state != this.state[pin].state) {
        this.sendDigital(pin, state.state);
      }
      if (state.value !== undefined && state.value != this.state[pin].value) {
        this.sendAnalog(pin, parseFloat(state.value));
      }
    }

    return active;
  };

  // --- reset ----------------------------------------------------------------

  async resetAll() {
    if (!this.isConnected()) return;

    for (const uid of Object.keys(this.triggersStates)) {
      this.stopTriggers(uid);
    }
    this.triggersActive = {};
    this.triggersStates = {};

    this.lockedStates = new Set();
    this.waitFor = [];
    this.uiDefaults = new Map();

    const prev = this.state;
    const next = deep_copy(getDefaultControllerState());

    // keep hardware inputs as-is
    for (const [k, pin] of Object.entries(prev)) {
      if (pin.mode === MODE.INPUT && next[k]) next[k] = { ...pin };
    }

    this.setState(next);
    await this.netWorkService?.sync?.();
  }

  // --- trigger engine --------------------------------------------------------

  checkLoaded() {
    let done = true;
    for (let defer of this.waitFor) {
      if (!defer.isResolved && !defer.isRejected) done = false;
    }
    if (done) {
      this.startAllTriggers();
    }
  }

  queueTriggers(uid, triggers) {
    this.triggersStates[uid] = triggers;
    this.triggersActive[uid] = [];
    this.triggerAnyStates[uid] = [];
    this.triggerConditionStates[uid] = [];

    // If a cleanup was scheduled for this uid but the widget becomes active
    // again before the batched cleanup runs, do not clear the newly queued fault.
    if (triggers?.length) {
      this.pendingClearTriggerUids.delete(uid);
    }
  }

  clearTriggersAndReset = async (uids = null) => {
    const triggerUids = (uids || Object.keys(this.triggersStates)).filter(
      (uid) => this.triggersStates[uid] !== undefined
    );

    if (!triggerUids.length) return;

    for (const uid of triggerUids) {
      this.queueTriggers(uid, []);
    }

    await this.requestReset();

    for (const uid of triggerUids) {
      this.stopTriggers(uid);
    }
  };

  scheduleClearTriggersAndReset = (uid) => {
    if (!uid || this.triggersStates[uid] === undefined) return;

    this.pendingClearTriggerUids.add(uid);

    if (this.clearTriggersTimer) {
      clearTimeout(this.clearTriggersTimer);
    }

    this.clearTriggersTimer = setTimeout(async () => {
      const uids = [...this.pendingClearTriggerUids];

      this.pendingClearTriggerUids.clear();
      this.clearTriggersTimer = null;

      await this.clearTriggersAndReset(uids);
    }, 0);
  };

  initializeTriggers(triggerStates = this.triggersStates) {
    // A fresh baseline releases old actions without changing their outputs.
    const lockedPins = [...this.lockedStates];
    this.lockedStates.clear();
    for (const name of lockedPins) {
      this.onStatelock.next({ name, locked: false });
    }
    for (const [uid, triggers] of Object.entries(triggerStates)) {
      this.queueTriggers(uid, triggers);
    }
    // Baseline every widget before any unconditional action can update inputs.
    this.startAllTriggers({ baselineOnly: true });
    this.startAllTriggers();
  }

  _processTriggers(uid, triggers, { baselineOnly = false } = {}) {
    if (!triggers) return;

    if (!this.triggersStates[uid]) {
      this.queueTriggers(uid, triggers);
    }

    // Input/UI updates can arrive before the hardware is ready. Keep the
    // triggers queued without consuming their activation until connection.
    if (!this.isConnected()) return;

    if (
      !this.triggersActive[uid] ||
      this.triggersActive[uid].length !== triggers.length
    ) {
      this.triggersActive[uid] = Array(triggers.length).fill(false);
    }

    if (!this.triggerAnyStates[uid]) this.triggerAnyStates[uid] = [];
    if (!this.triggerConditionStates[uid])
      this.triggerConditionStates[uid] = [];

    for (const [i, trigger] of triggers.entries()) {
      // ANY is an edge condition: the first known level is only a baseline.
      // Save levels before actions, whose writes synchronously notify us again.
      const anyConditions = (trigger.conditions || []).filter(
        (condition) =>
          condition.mode === 'digital' && condition.value === undefined
      );
      const anyStates = anyConditions.map(
        (condition) => this.state[condition.pin]?.state
      );
      const previousAnyStates = this.triggerAnyStates[uid][i];
      const anyChanged = anyStates.some(
        (state, index) =>
          typeof state === 'boolean' &&
          typeof previousAnyStates?.[index] === 'boolean' &&
          state !== previousAnyStates[index]
      );
      this.triggerAnyStates[uid][i] = anyStates;

      let active = true;
      if (trigger.conditions?.length) {
        if (!this.checkTrigger(trigger)) active = false;
      }

      const previousConditionState = this.triggerConditionStates[uid][i];
      this.triggerConditionStates[uid][i] = active;
      if (baselineOnly) continue;

      if (
        active &&
        (anyConditions.length
          ? anyChanged
          : !this.triggersActive[uid][i] &&
            (!trigger.conditions?.length || !previousConditionState))
      ) {
        // A permanent action must be able to refresh its own output on the
        // next ANY edge, then lock it again after the write.
        if (this.triggersActive[uid][i]) {
          for (const action of trigger.actions || []) {
            if (action.frequency === 'permanent') {
              this.lockedStates.delete(action.pin);
              this.onStatelock.next({ name: action.pin, locked: false });
            }
          }
        }
        this.triggersActive[uid][i] = true;
        if (trigger.actions) {
          for (let action of trigger.actions) {
            switch (action.action) {
              case 'set':
                switch (action.mode) {
                  case 'analog':
                    this.sendAnalog(action.pin, parseFloat(action.value));
                    if (action.frequency === 'permanent') {
                      this.lockedStates.add(action.pin);
                      this.onStatelock.next({ name: action.pin, locked: true });
                    }
                    break;
                  case 'digital':
                    this.sendDigital(action.pin, action.value);
                    if (action.frequency === 'permanent') {
                      this.lockedStates.add(action.pin);
                      this.onStatelock.next({ name: action.pin, locked: true });
                    }
                    break;
                  case 'servo':
                    this.sendServo(action.pin, parseFloat(action.value));
                    if (action.frequency === 'permanent') {
                      this.lockedStates.add(action.pin);
                      this.onStatelock.next({ name: action.pin, locked: true });
                    }
                    break;
                }
                break;
              case 'reset':
                this.sendReset();
                break;
            }
          }
        }
        if (trigger.script) {
          try {
            this.runScript(trigger.script);
          } catch (e) {
            console.error(e);
          }
        }
        this.onTrigger.next({ trigger, state: true });
        this.sendDigital(trigger.name, true);
      } else if (!active && this.triggersActive[uid][i]) {
        this.triggersActive[uid][i] = false;
        this.onTrigger.next({ trigger, state: false });

        // A condition becoming false only releases permanent locks. Output
        // changes must be configured as actions on a matching trigger.
        for (const action of trigger.actions || []) {
          if (action.frequency === 'permanent') {
            this.lockedStates.delete(action.pin);
            this.onStatelock.next({ name: action.pin, locked: false });
          }
        }

        this.sendDigital(trigger.name, false);
      }
    }
  }

  sleep = sleep;

  runScript = async (script) => {
    var result = function (str) {
      eval(`(async () => {
        try {
          ${str}
        } catch(e) {
          console.error(e);
        }
      })()`);
    }.call(this, script);
  };

  stopTriggers(uid, opts = {}) {
    const { deactivate = false } = opts;

    try {
      const triggers = this.triggersStates[uid] || [];
      const active = this.triggersActive[uid] || [];

      if (deactivate) {
        for (const [i, trigger] of triggers.entries()) {
          if (!active[i]) continue;

          if (trigger.actions) {
            for (const action of trigger.actions) {
              if (action.frequency === 'permanent') {
                this.lockedStates.delete(action.pin);
                this.onStatelock.next({ name: action.pin, locked: false });
              }

              if (action.action === 'set') {
                switch (action.mode) {
                  case 'analog':
                    this.lockedStates.delete(action.pin);
                    this.sendAnalog(action.pin, 0);
                    break;

                  case 'digital':
                    this.lockedStates.delete(action.pin);
                    this.sendDigital(action.pin, !action.value);
                    break;

                  case 'servo':
                    this.lockedStates.delete(action.pin);
                    this.sendServo(action.pin, 0);
                    break;
                }
              }
            }
          }

          if (trigger.name) {
            this.lockedStates.delete(trigger.name);
            this.sendDigital(trigger.name, false);
          }

          this.onTrigger.next({ trigger, state: false });
        }
      }
    } catch (e) {
      console.error(e);
    }

    delete this.triggersActive[uid];
    delete this.triggerAnyStates[uid];
    delete this.triggerConditionStates[uid];
    delete this.triggersStates[uid];
  }

  // --- I/O public API --------------------------------------------------------

  isStateLocked = (name) => {
    return this.lockedStates.has(name);
  };

  sendAnalog = (name, value) => {
    if (this.lockedStates.has(name))
      return console.error('State', name, 'is locked by a trigger');

    if (!this.state[name]) this.assertState(name);

    const pin = this.state[name];

    // leaving SERVO -> restore default type/mode (usually pwm_output)
    if (pin.type === 'servo_output') {
      this._restoreFromDefault(name, 'pwm_output');
    }

    if (!isWritable(pin)) {
      return console.warn(
        'Refusing to write to non-writable pin',
        name,
        pin?.type
      );
    }

    pin.value = value;
    this.setAnalog(name, value);
    this.setDigital(name, value > (this.state.threshold || 4));

    if (isHardwareWritable(pin, name)) {
      this.netWorkService.emit('sendAnalog', name, value);
    }
  };

  sendDigital = (name, state) => {
    if (this.lockedStates.has(name))
      return console.error('State', name, 'is locked by a trigger');

    if (!this.state[name]) this.assertState(name);

    const pin = this.state[name];

    // leaving SERVO -> restore default type/mode
    if (pin.type === 'servo_output') {
      const def = defaultControllerState?.[name];

      if (def?.type === 'pwm_output') {
        pin.type = 'digital_output';
        pin.mode = MODE.OUTPUT;
      } else {
        this._restoreFromDefault(name, 'digital_output');
      }

      pin.servoConfigured = false;
    }

    if (!isWritable(pin)) {
      return console.warn(
        'Refusing to write to non-writable pin',
        name,
        pin?.type
      );
    }

    pin.state = state;
    this.setDigital(name, state);
    this.setAnalog(name, state * 24);

    if (isHardwareWritable(pin, name)) {
      this.netWorkService.emit('sendDigital', name, state);
    }
  };

  // NOTE: These are socket-only in your original code; with WebSerial they are no-ops by default.
  // Keep them if you still rely on them somewhere, but they won't work unless you reintroduce a socket service.
  sendStream = (name) => {
    console.warn('sendStream is not implemented for WebSerial transport', name);
  };

  sendModeAnalog = (name, mode) => {
    console.warn(
      'sendModeAnalog is not implemented for WebSerial transport',
      name,
      mode
    );
  };

  sendModeDigital = (name, mode) => {
    console.warn(
      'sendModeDigital is not implemented for WebSerial transport',
      name,
      mode
    );
  };

  requestReset = () => {
    const reset = this.resetQueue
      .catch((e) => {
        console.error('Previous box reset failed', e);
      })
      .then(() => this.sendReset())
      .catch((e) => {
        console.error('Box reset failed', e);
      });

    this.resetQueue = reset;
    return reset;
  };

  waitForInputSettle = async () => {
    if (this.netWorkService?.waitForInputSettle) {
      await this.netWorkService.waitForInputSettle();
      return;
    }

    // Preserve the original correction delay for the legacy network
    // transport, which does not expose an input-settle helper.
    await sleep(100);
  };

  sendReset = async () => {
    const jitter = () => sleep(Math.random() * 30);

    const prev = this.state;
    const triggerStates = deep_copy(this.triggersStates);

    for (const uid of Object.keys(triggerStates)) this.stopTriggers(uid);

    await jitter();
    this.lockedStates = new Set();

    const next = deep_copy(getDefaultControllerState());

    // keep hardware inputs as-is
    for (const [k, pin] of Object.entries(prev)) {
      if (pin.mode === MODE.INPUT && next[k]) next[k] = { ...pin };
    }

    // re-apply ONLY registered UI defaults to outputs
    for (const [name, def] of this.uiDefaults.entries()) {
      const p = next[name];
      if (p && p.mode !== MODE.INPUT) {
        p.state = !!def;
        if (typeof p.value === 'number') p.value = def ? 24 : 0;
      }
    }

    await jitter();
    this.setState(next);
    await jitter();

    await this.netWorkService?.sync?.();
    await jitter();

    this.initializeTriggers(triggerStates);
  };

  startAllTriggers(options) {
    let triggerStates = deep_copy(this.triggersStates);
    for (let uid of Object.keys(triggerStates)) {
      this._processTriggers(uid, triggerStates[uid], options);
    }
  }

  sendPwmFrequency = (name, value) => {
    // this.netWorkService.emit("sendPwmFrequency", name, value);
  };

  // --- browser audio ---------------------------------------------------------

  playSoundFile = async (src, opts = {}) => {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') {
      console.warn('Audio files can only be played in the browser');
      return;
    }

    if (!src) {
      return console.warn('Missing audio file src');
    }

    const audio = new Audio(src);

    audio.volume = Math.max(0, Math.min(1, Number(opts.volume ?? 1)));
    audio.loop = !!opts.loop;

    if (opts.playbackRate != null) {
      const playbackRate = Number(opts.playbackRate);
      if (Number.isFinite(playbackRate) && playbackRate > 0) {
        audio.playbackRate = playbackRate;
      }
    }

    try {
      await audio.play();
    } catch (e) {
      console.warn('Could not play audio file', src, e);
    }

    if (opts.wait !== false) {
      await new Promise((resolve) => {
        audio.addEventListener('ended', resolve, { once: true });
        audio.addEventListener('error', resolve, { once: true });
      });
    }

    return audio;
  };

  // --- constructor -----------------------------------------------------------

  constructor(props) {
    this.boxConnected.next(false);

    // WebSerial transport
    this.netWorkService = new WebSerial(this);

    this.boxConnected.subscribe((boxConnected) => {
      if (boxConnected) {
        this.initializeTriggers();
      }
    });

    const checkAllTriggers = () => this.startAllTriggers();
    this.onDigital.subscribe(checkAllTriggers);
    this.onAnalog.subscribe(checkAllTriggers);
    this.onState.subscribe(checkAllTriggers);

    if (BoxIoService._instance) return BoxIoService._instance;
    BoxIoService._instance = this;
  }
}

BoxIoService.getInstance = function (props) {
  return BoxIoService._instance || new BoxIoService(props);
};

export { BoxIoService, digitalOutputs, analogInputs };
